import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { userInitialsAsString } from '../../util/data';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';

import {
  AvatarMedium,
  H2,
  LayoutSingleColumn,
  NamedLink,
  Page,
  ResponsiveImage,
} from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import flourishImage from '../../assets/nextmine-flourish-gold.svg';
import css from './CollectionsPage.module.css';

const PREVIEW_PIECES = 4;

/**
 * Grupperer stykker pr. samler. Rækkefølgen følger stykkernes (nyeste først),
 * så samlere der lige har lagt noget op, står øverst.
 */
export const groupByCollector = pieces => {
  const byAuthor = new Map();

  pieces.forEach(piece => {
    const author = piece.author;
    const authorId = author?.id?.uuid;
    if (!authorId) {
      return;
    }
    if (!byAuthor.has(authorId)) {
      byAuthor.set(authorId, { authorId, author, pieces: [] });
    }
    byAuthor.get(authorId).pieces.push(piece);
  });

  return Array.from(byAuthor.values());
};

const cardVariants = image =>
  Object.keys(image?.attributes?.variants || {}).filter(k => k.startsWith('listing-card'));

export const CollectionsPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const { scrollingDisabled, pieces, queryError, queryInProgress } = props;

  const collectors = groupByCollector(pieces);
  const title = intl.formatMessage({ id: 'CollectionsPage.title' });

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={title}
      description={intl.formatMessage(
        { id: 'CollectionsPage.description' },
        { marketplaceName: config.marketplaceName }
      )}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <header className={css.header}>
            <H2 as="h1" className={css.heading}>
              <FormattedMessage id="CollectionsPage.heading" />
            </H2>
            <p className={css.intro}>
              <FormattedMessage id="CollectionsPage.intro" />
            </p>
            <NamedLink className={css.cta} name="MyCollectionPage">
              <FormattedMessage id="CollectionsPage.shareYours" />
            </NamedLink>
          </header>

          {queryError ? (
            <p className={css.error}>
              <FormattedMessage id="CollectionsPage.loadingFailed" />
            </p>
          ) : collectors.length > 0 ? (
            <ul className={css.collectors}>
              {collectors.map(({ authorId, author, pieces: collectorPieces }) => {
                // Nextmine: samlere vises kun med initialer.
                const displayName = userInitialsAsString(author, '');
                return (
                  <li className={css.collector} key={authorId}>
                    <NamedLink
                      className={css.collectorLink}
                      name="CollectionPage"
                      params={{ id: authorId }}
                    >
                      <div className={css.collectorHeader}>
                        <AvatarMedium user={author} disableProfileLink />
                        <div>
                          <span className={css.collectorName}>{displayName}</span>
                          <span className={css.collectorCount}>
                            <FormattedMessage
                              id="CollectionsPage.pieceCount"
                              values={{ count: collectorPieces.length }}
                            />
                          </span>
                        </div>
                      </div>
                      <div className={css.previewStrip}>
                        {collectorPieces.slice(0, PREVIEW_PIECES).map(piece => (
                          <div className={css.previewThumb} key={piece.id.uuid}>
                            <ResponsiveImage
                              rootClassName={css.previewImage}
                              alt={piece.attributes.title}
                              image={piece.images?.[0]}
                              variants={cardVariants(piece.images?.[0])}
                              sizes="140px"
                            />
                          </div>
                        ))}
                      </div>
                    </NamedLink>
                  </li>
                );
              })}
            </ul>
          ) : queryInProgress ? null : (
            <p className={css.emptyNote}>
              <FormattedMessage id="CollectionsPage.empty" />
            </p>
          )}

          <img src={flourishImage} alt="" className={css.flourish} />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { pieceRefs, queryError, queryInProgress } = state.CollectionsPage;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    pieces: getMarketplaceEntities(state, pieceRefs),
    queryError,
    queryInProgress,
  };
};

const CollectionsPage = compose(connect(mapStateToProps))(CollectionsPageComponent);

export default CollectionsPage;
